import { useState, useEffect } from 'react';
import { Download, X, WifiOff } from 'lucide-react';

// PWA Install Prompt Component
export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwaPromptDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Don't show for 7 days after dismissal
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // For iOS, show custom prompt after delay
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // For other browsers, listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="install-prompt" data-testid="pwa-install-prompt">
      <div className="flex items-center gap-3">
        <Download className="w-6 h-6 flex-shrink-0" />
        <div>
          <p className="font-semibold text-sm">Install EquiVault</p>
          <p className="text-xs opacity-90">
            {isIOS 
              ? 'Tap Share then "Add to Home Screen"' 
              : 'Get the app for quick access'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!isIOS && (
          <button onClick={handleInstall} data-testid="pwa-install-btn">
            Install
          </button>
        )}
        <button onClick={handleDismiss} className="dismiss" data-testid="pwa-dismiss-btn">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Offline Indicator Component
export const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className={`offline-indicator ${isOffline ? 'show' : ''}`} data-testid="offline-indicator">
      <WifiOff className="w-4 h-4 inline mr-2" />
      You're offline. Some features may be unavailable.
    </div>
  );
};
